/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

import _t from 'i18n!nls/xdp';

type Props = { detailItems?: React.ReactNode[] };

export function getHeadingStyle(enableXDPRedesignPhase1: boolean) {
  return enableXDPRedesignPhase1 ? 'bodySecondary' : 'subtitleMedium';
}

const styles = {
  container: css`
    padding: 0 0 var(--cds-spacing-200) 0;
    margin-top: calc(var(--cds-spacing-300) * -1);

    ${breakpoints.down('sm')} {
      margin-top: calc(var(--cds-spacing-100) * -1);
    }
  `,
  containerRedesign: css`
    padding: 0 0 var(--cds-spacing-200) 0;

    ${breakpoints.down('sm')} {
      margin-top: calc(var(--cds-spacing-100) * -1);
    }
  `,
  item: css`
    margin-top: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-top: 0;
    }
  `,
  inlineItem: css`
    display: inline-flex;
    flex-wrap: wrap;

    // item title
    > div {
      margin-top: calc((var(--cds-spacing-400) + var(--cds-spacing-50)) * -1);
      margin-left: calc((var(--cds-spacing-400) + var(--cds-spacing-50)));
      width: 100%;
    }

    // item description / content
    > div + * {
      margin-top: calc(var(--cds-spacing-100) * -1);
      margin-left: calc((var(--cds-spacing-400) + var(--cds-spacing-50)));
    }
  `,
};

const Details = ({ detailItems }: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework, enableXDPRedesignHero, enableXDPRedesignKIM } =
    useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  const enableBestOfBreed = enableXDPRedesignDescriptionRework && enableXDPRedesignHero && enableXDPRedesignKIM;
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  if (!detailItems?.length) {
    return null;
  }

  const smBp = enableXDPRedesign ? 12 : 6;
  const mdBp = enableXDPRedesign ? 12 : 4;
  let inline = false;
  if (enableXDPRedesign || isMobile) {
    inline = true;
  }

  const title = enableXDPRedesign ? _t('What’s included') : _t('Details to know');

  return (
    <Subsection
      title={
        <Typography2 variant={enableBestOfBreed ? 'subtitleMedium' : 'subtitleLarge'} component="h2">
          {title}
        </Typography2>
      }
      spacing={enableXDPRedesign ? 16 : 24}
    >
      <Grid container css={enableXDPRedesign ? styles.containerRedesign : styles.container} spacing={16}>
        {detailItems
          .filter((item) => !!item)
          .map((item) => {
            return (
              // Adding an index key prevents updates when they would be appropriate.
              // eslint-disable-next-line react/jsx-key>
              <Grid item xs={12} sm={smBp} md={mdBp} css={inline ? styles.inlineItem : styles.item}>
                {item}
              </Grid>
            );
          })}
      </Grid>
    </Subsection>
  );
};

export default Details;
